import React, { useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';
import { getVideoUrl } from '../config/assets';
import { authenticatedFetch } from '../config/authenticatedFetch';

interface PhraseData {
  id: string;
  phrase: string;
  author: string;
  category: string;
  person_id: string;
  external_id: string;
}

interface PhraseCloudItem {
  id: string;
  text: string;
  lines: string[];
  x: number;
  y: number;
  fontSize: number;
  opacity: number;
  lifeTime: number;
  showTime: number;
}

const PainelPalavras2: React.FC = () => {
  const [phrases, setPhrases] = useState<PhraseCloudItem[]>([]);
  const [newPhrases, setNewPhrases] = useState<PhraseData[]>([]);
  const [oldPhrases, setOldPhrases] = useState<PhraseData[]>([]);
  const [windowSize, setWindowSize] = useState({ width: window.innerWidth, height: window.innerHeight });

  // Frases de teste para garantir que sempre temos conteúdo
  const testPhrases: PhraseData[] = [
    // { id: 'test1', phrase: 'Esta é uma frase de teste para verificar o funcionamento', author: 'Sistema', category: 'test', person_id: '1', external_id: '1' },
    // { id: 'test2', phrase: '"Painel de palavras funcionando corretamente"', author: 'Sistema', category: 'test', person_id: '2', external_id: '2' },
    // { id: 'test3', phrase: 'Sistema de distribuição equilibrada implementado', author: 'Sistema', category: 'test', person_id: '3', external_id: '3' },
    // { id: 'test4', phrase: '"Quebra de linha automática para textos longos', author: 'Sistema', category: 'test', person_id: '4', external_id: '4' },
    // { id: 'test5', phrase: 'Grid 6x4 para posicionamento"', author: 'Sistema', category: 'test', person_id: '5', external_id: '5' }
  ];

  // Redimensionamento da tela
  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth, height: window.innerHeight });
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Função para garantir aspas duplas no texto (sem duplicar)
  const ensureQuotes = useCallback((text: string): string => {
    if (!text) return text;
    
    const trimmedText = text.trim();
    const hasStartQuote = trimmedText.startsWith('"');
    const hasEndQuote = trimmedText.endsWith('"');
    
    let result = trimmedText;
    
    if (!hasStartQuote) {
      result = '"' + result;
    }
    
    if (!hasEndQuote) {
      result = result + '"';
    }
    
    return result;
  }, []);

  // Função para quebrar texto em linhas (máximo 80 caracteres)
  const breakTextIntoLines = useCallback((text: string): string[] => {
    if (text.length <= 80) {
      return [text];
    }
    
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';
    
    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      
      if (testLine.length <= 80) {
        currentLine = testLine;
      } else {
        if (currentLine) {
          lines.push(currentLine);
          currentLine = word;
        } else {
          lines.push(word);
        }
      }
    }
    
    if (currentLine) {
      lines.push(currentLine);
    }
    
    return lines;
  }, []);

  // Sistema de posicionamento simples e equilibrado - apenas nos 3/5 inferiores
  const findFreePosition = useCallback((existingPhrases: PhraseCloudItem[], textLines: string[], fontSize: number) => {
    const margin = 30;
    const availableWidth = windowSize.width - (margin * 2);
    
    // Área dos 3/5 inferiores da tela (40% do topo até 100%)
    const topOffset = windowSize.height * 0.4; // 2/5 superiores reservados para o cabeçalho
    const availableHeight = (windowSize.height * 0.6) - (margin * 2); // 3/5 inferiores para as frases
    
    const lineHeight = fontSize * 1.4;
    const textHeight = textLines.length * lineHeight;
    const textWidth = Math.max(...textLines.map(line => line.length)) * (fontSize * 0.6);
    
    // Dividir área disponível em grid 6x3 para distribuição equilibrada
    const cols = 7; // Mantido 7 colunas
    const rows = 4; // Reduzido para 3 linhas nos 3/5 inferiores
    const cellWidth = availableWidth / cols;
    const cellHeight = availableHeight / rows;
    
    // Tentar cada célula do grid na área dos 3/5 inferiores
    for (let row = 0; row < rows; row++) {
      for (let col = 0; col < cols; col++) {
        const cellX = margin + (col * cellWidth);
        const cellY = topOffset + margin + (row * cellHeight); // Posicionar após o cabeçalho
        
        // Posição aleatória dentro da célula
        const x = cellX + Math.random() * Math.max(cellWidth - textWidth, 10);
        const y = cellY + Math.random() * Math.max(cellHeight - textHeight, 10);
        
        // Verificar se não sobrepõe
        const hasCollision = existingPhrases.some(phrase => {
          const phraseWidth = Math.max(...phrase.lines.map(line => line.length)) * (phrase.fontSize * 0.6);
          const phraseHeight = phrase.lines.length * phrase.fontSize * 1.4;
          
          return !(x > phrase.x + phraseWidth + 20 || 
                  x + textWidth + 20 < phrase.x ||
                  y > phrase.y + phraseHeight + 20 || 
                  y + textHeight + 20 < phrase.y);
        });
        
        if (!hasCollision) {
          return { x, y };
        }
      }
    }
    
    return null;
  }, [windowSize]);

  // Buscar frases da API
  const fetchPhrases = useCallback(async () => {
    try {
      console.log('🔄 Buscando frases da API...');
      const response = await authenticatedFetch(API_ENDPOINTS.GET_PHRASES);
      console.log('📡 Response status:', response.status);
      
      if (response.ok) {
        const data = await response.json();
        console.log('📝 Frases recebidas:', data);
        
        if (data.new && data.old) {
          console.log(`✅ Frases processadas - New: ${data.new.length}, Old: ${data.old.length}`);
          setNewPhrases(data.new);
          setOldPhrases(data.old);
        } else if (Array.isArray(data)) {
          console.log(`✅ Frases processadas como array - Total: ${data.length}`);
          setOldPhrases(data);
          setNewPhrases([]);
        } else {
          console.warn('⚠️ Formato de dados não reconhecido:', data);
          // Usar frases de teste se não conseguir processar os dados da API
          console.log('🔄 Usando frases de teste...');
          setOldPhrases(testPhrases);
          setNewPhrases([]);
        }
      } else {
        console.error('❌ Erro na resposta da API:', response.status, response.statusText);
        // Usar frases de teste se a API falhar
        console.log('🔄 API falhou, usando frases de teste...');
        setOldPhrases(testPhrases);
        setNewPhrases([]);
      }
    } catch (error) {
      console.error('💥 Erro ao buscar frases:', error);
      // Usar frases de teste se houver erro de rede
      console.log('🔄 Erro de rede, usando frases de teste...');
      setOldPhrases(testPhrases);
      setNewPhrases([]);
    }
  }, []);

  // Adicionar frase na tela
  const addPhrase = useCallback((phraseData: PhraseData) => {
    setPhrases(current => {
      // Verificar se já existe
      if (current.find(p => p.id === phraseData.id)) {
        return current;
      }
      
      // Garantir aspas na frase
      const phraseWithQuotes = ensureQuotes(phraseData.phrase);
      const lines = breakTextIntoLines(phraseWithQuotes);
      const fontSize = 20 + Math.random() * 15; // Entre 20 e 35px
      const position = findFreePosition(current, lines, fontSize);
      
      if (!position) {
        console.log('Não foi possível posicionar frase:', phraseWithQuotes.substring(0, 30));
        return current;
      }
      
      const newPhrase: PhraseCloudItem = {
        id: phraseData.id,
        text: phraseWithQuotes,
        lines,
        x: position.x,
        y: position.y,
        fontSize,
        opacity: 0.7 + Math.random() * 0.3,
        lifeTime: 10000 + Math.random() * 15000,
        showTime: Date.now()
      };
      
      console.log('Frase adicionada:', phraseWithQuotes.substring(0, 30), 'em', position);
      return [...current, newPhrase];
    });
  }, [ensureQuotes, breakTextIntoLines, findFreePosition]);

  // Gerenciar ciclo de vida das frases
  useEffect(() => {
    const interval = setInterval(() => {
      const now = Date.now();
      setPhrases(current => 
        current.filter(phrase => (now - phrase.showTime) < phrase.lifeTime)
      );
    }, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Buscar frases periodicamente e inicializar
  useEffect(() => {
    console.log('🎬 Iniciando PainelPalavras2...');
    
    // Buscar imediatamente
    fetchPhrases();
    
    // Adicionar frases de teste imediatamente para mostrar algo na tela
    setTimeout(() => {
      if (phrases.length === 0) {
        console.log('🚀 Adicionando frases de teste iniciais...');
        testPhrases.slice(0, 3).forEach((phrase, index) => {
          setTimeout(() => addPhrase(phrase), index * 300);
        });
      }
    }, 1000);
    
    const interval = setInterval(fetchPhrases, 10000);
    return () => clearInterval(interval);
  }, [fetchPhrases]);

  // Adicionar frases gradualmente
  useEffect(() => {
    const allPhrases = [...newPhrases, ...oldPhrases];
    console.log(`🎯 Total de frases disponíveis: ${allPhrases.length}`);
    
    if (allPhrases.length === 0) {
      console.log('⚠️ Nenhuma frase disponível para exibir');
      return;
    }
    
    // Adicionar algumas frases iniciais imediatamente
    if (phrases.length === 0 && allPhrases.length > 0) {
      console.log('🚀 Adicionando frases iniciais...');
      const initialCount = Math.min(5, allPhrases.length);
      for (let i = 0; i < initialCount; i++) {
        setTimeout(() => {
          const randomPhrase = allPhrases[Math.floor(Math.random() * allPhrases.length)];
          addPhrase(randomPhrase);
        }, i * 500);
      }
    }
    
    const interval = setInterval(() => {
      if (phrases.length < 15) { // Máximo 15 frases na tela
        const availablePhrases = allPhrases.filter(p => 
          !phrases.find(existing => existing.id === p.id)
        );
        
        if (availablePhrases.length > 0) {
          const randomPhrase = availablePhrases[Math.floor(Math.random() * availablePhrases.length)];
          addPhrase(randomPhrase);
        }
      }
    }, 3000);
    
    return () => clearInterval(interval);
  }, [newPhrases, oldPhrases, phrases, addPhrase]);

  return (
    <div style={{ 
      width: '100vw', 
      height: '100vh', 
      overflow: 'hidden', 
      position: 'fixed', 
      top: 0, 
      left: 0,
      margin: 0,
      padding: 0,
      backgroundColor: '#000'
    }}>
      {/* Vídeo de fundo */}
      <video
        autoPlay
        loop
        muted
        playsInline
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          zIndex: 1
        }}
      >
        <source src={getVideoUrl('Tunnel_fundo_sem_amarelo_Ajuste.mp4')} type="video/mp4" />
        <source src={getVideoUrl('background.webm')} type="video/webm" />
      </video>

      {/* Cabeçalho fixo nos 2/5 superiores */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '40%', // 2/5 da tela
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 2,
        pointerEvents: 'none'
      }}>
        <h1 style={{
          fontSize: `${Math.min(windowSize.width * 0.055, 110)}px`,
          color: 'white',
          fontFamily: 'ACTIVE-GROTESK, sans-serif',
          textAlign: 'center',
          margin: 0,
          padding: '0 20px',
          lineHeight: 1.2
        }}>
          Levar mais vida é...
        </h1>
      </div>

      {/* Frases nos 3/5 inferiores */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        zIndex: 2,
        pointerEvents: 'none'
      }}>
        {phrases.map((phrase) => (
          <div
            key={phrase.id}
            style={{
              position: 'absolute',
              left: phrase.x,
              top: phrase.y,
              fontSize: `${phrase.fontSize}px`,
              color: 'white',
              textShadow: '2px 2px 4px rgba(0,0,0,0.8)',
              opacity: phrase.opacity,
              fontFamily: 'BASKVILL, serif',
              lineHeight: 1.4,
              whiteSpace: 'pre-line'
            }}
          >
            {phrase.lines.join('\n')}
          </div>
        ))}
      </div>

      {/* Debug info 
      <div style={{
        position: 'absolute',
        top: 10,
        left: 10,
        color: 'white',
        fontSize: '14px',
        backgroundColor: 'rgba(0,0,0,0.8)',
        padding: '10px',
        borderRadius: '5px',
        zIndex: 3,
        border: '1px solid white'
      }}>
        <div>📍 Layout: Cabeçalho (2/5) + Frases (3/5)</div>
        <div>Frases na tela: {phrases.length}</div>
        <div>New: {newPhrases.length} | Old: {oldPhrases.length}</div>
        <div>Tamanho da tela: {windowSize.width}x{windowSize.height}</div>
        <div>Status: {phrases.length > 0 ? '✅ Funcionando' : '⏳ Carregando...'}</div>
      </div>*/}
    </div>
  );
};

export default PainelPalavras2;